'use-client';
import * as React from 'react';
import classNames from 'clsx';
import {
  formatClassNames,
  useVideoAPI,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import type { MediaContainerVideoAPI } from '@wix/editor-elements-types/components';
import MeshContainer from '../../../thunderbolt-core-components/MeshContainer/viewer/MeshContainer';
import FillLayers from '../../FillLayers/viewer/FillLayers';
import type { ClassicSectionProps } from '../ClassicSection.types';
import ShapeDividers from '../../MediaContainers/ShapeDividers/viewer/shapeDividers';
import semanticClassNames from '../ClassicSection.semanticClassNames';
import styles from './style/ClassicSection.scss';
import { PauseSmall, PlaySmall } from '@wix/wix-ui-icons-common';

const ClassicSection: React.ForwardRefRenderFunction<
  MediaContainerVideoAPI,
  ClassicSectionProps
> = (props, compRef) => {
  const {
    id,
    fillLayers = {},
    className,
    customClassNames = [],
    meshProps = {
      wedges: [],
      rotatedComponents: [],
    },
    anchorUrlFragment,
    children,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onDblClick,
    getPlaceholder,
    a11y = {},
    onStop,
    onReady,
    dividers,
    lang,
    translations,
    isPlayPauseSectionExperimentOn,
  }: ClassicSectionProps = props;

  const sdkEventHandlers = {
    onMouseEnter,
    onMouseLeave,
    onClick,
    onDoubleClick: onDblClick,
  };

  // fix content in front of background in position:fixed disappearing when scrolling to it - Chromium +85 bug
  const shouldFixContentFlashing = fillLayers.hasBgFullscreenScrollEffect;

  const hasVideo = !!fillLayers.video;
  const videoRef = useVideoAPI(compRef, hasVideo, onStop);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { tabindex, ...restA11y } = a11y;
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(
    !videoRef?.current?.paused || false,
  );

  const handlePlayPause = () => {
    const video = videoRef?.current;
    if (video) {
      video.paused ? video.play() : video.pause();
      setIsVideoPlaying(prev => {
        const newPlayingState = !prev;

        // Find and control all child video elements
        const sectionElement = document.getElementById(id);
        if (sectionElement) {
          const childVideos = sectionElement.querySelectorAll('video');
          childVideos.forEach(childVideo => {
            if (childVideo !== video) {
              newPlayingState ? childVideo.play() : childVideo.pause();
            }
          });
        }

        return newPlayingState;
      });
    }
  };

  return (
    <section
      id={id}
      {...getDataAttributes(props)}
      {...sdkEventHandlers}
      {...restA11y}
      {...getTabIndexAttribute(a11y)}
      className={classNames(
        styles.container,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      data-block-level-container="ClassicSection"
      lang={lang}
    >
      {anchorUrlFragment && (
        <div className={styles.anchorElement} id={anchorUrlFragment} />
      )}
      {hasVideo && isPlayPauseSectionExperimentOn && (
        <button
          className={styles.videoPlayPauseButton}
          onClick={handlePlayPause}
          aria-label={translations.ariaLabel}
          aria-pressed={isVideoPlaying}
        >
          {isVideoPlaying ? <PauseSmall /> : <PlaySmall />}
        </button>
      )}
      <FillLayers
        {...fillLayers}
        onReady={onReady}
        getPlaceholder={getPlaceholder}
        videoRef={videoRef}
        onClick={handlePlayPause}
      />
      {dividers && <ShapeDividers {...dividers} />}

      <MeshContainer
        id={id}
        {...meshProps}
        extraClassName={classNames({
          [styles.fixFlashingContent]: shouldFixContentFlashing,
        })}
      >
        {children}
      </MeshContainer>
    </section>
  );
};

export default React.forwardRef(ClassicSection);
